import type { StructuredDescription, FormState } from "../types";
import { Box, Group, Stack, Text, Textarea, TextInput, Paper } from "@mantine/core";
import { StaffSelect } from "./StaffSelect";

const LABEL_W = 180;

function InlineField({ label, value, onChange, warnings }: { label: string; value: string; onChange: (v: string) => void; warnings?: string[] }) {
  return (
    <Stack gap={4}>
      <Group gap="md" wrap="nowrap" align="center">
        <Text w={LABEL_W} fw={600} c={value ? undefined : "dimmed"} style={{ flexShrink: 0 }}>{label}</Text>
        <TextInput style={{ flex: 1 }} value={value} onChange={e => onChange(e.currentTarget.value)} />
      </Group>
      {warnings?.map((msg, i) => <Text key={i} c="yellow.7" fz="sm" pl={LABEL_W + 16}>⚠ {msg}</Text>)}
    </Stack>
  );
}

export function DescriptionSection({ isManualMode, formState, structuredDesc, generatedDescription, warnings, onUpdateFormState, onUpdateStructuredDesc }: {
  isManualMode: boolean; formState: FormState; structuredDesc: StructuredDescription; generatedDescription: string; warnings: Record<string, string[]>;
  onUpdateFormState: (u: Partial<FormState>) => void; onUpdateStructuredDesc: (u: StructuredDescription) => void;
}) {
  if (isManualMode) {
    return <Textarea label="Описание видео" autosize minRows={8} maxRows={20} value={formState.description} onChange={e => onUpdateFormState({ description: e.currentTarget.value })} />;
  }

  const patchMeta = (k: 'date' | 'lecturer', val: string) => onUpdateStructuredDesc({ ...structuredDesc, metadata: { ...structuredDesc.metadata, [k]: val } });
  const patchRef = (k: 'youtube' | 'vk', val: string) => onUpdateStructuredDesc({ ...structuredDesc, references: { ...structuredDesc.references, [k]: val } });
  const patchStaff = (role: 'operator' | 'editor', name: string, id: string | null) => onUpdateStructuredDesc({ ...structuredDesc, staff: { ...structuredDesc.staff, [role]: name, [`${role}Id`]: id } });

  const textFields = [
    { label: "Дата лекции", value: structuredDesc.metadata.date, onChange: (v: string) => patchMeta('date', v), warn: warnings['metadata.date'] },
    { label: "Лектор", value: structuredDesc.metadata.lecturer, onChange: (v: string) => patchMeta('lecturer', v), warn: warnings['metadata.lecturer'] },
    { label: "Плейлист на YouTube", value: structuredDesc.references.youtube, onChange: (v: string) => patchRef('youtube', v), warn: warnings['references.youtube'] },
    { label: "Плейлист в ВК", value: structuredDesc.references.vk, onChange: (v: string) => patchRef('vk', v), warn: warnings['references.vk'] },
  ];

  return (
    <Group align="stretch" gap={0} wrap="nowrap">
      <Stack gap="md" style={{ flex: 1, minWidth: 0, borderRight: '1px solid var(--mantine-color-default-border)', paddingRight: 'var(--mantine-spacing-md)' }}>
        <Textarea label="Дополнительное сообщение" autosize minRows={3} maxRows={10} placeholder="Введите дополнительное сообщение" value={structuredDesc.additionalMessage} onChange={e => onUpdateStructuredDesc({ ...structuredDesc, additionalMessage: e.currentTarget.value })} />
        <Textarea label="Таймкоды" autosize minRows={5} maxRows={14} placeholder="Введите таймкоды" value={structuredDesc.timestamps} onChange={e => onUpdateStructuredDesc({ ...structuredDesc, timestamps: e.currentTarget.value })} />
        <InlineField {...textFields[0]} />
        <InlineField {...textFields[1]} />
        <StaffSelect label="Оператор" value={structuredDesc.staff.operator} studentId={structuredDesc.staff.operatorId ?? null} onChange={(n, id) => patchStaff('operator', n, id)} warnings={warnings['staff.operator']} />
        <StaffSelect label="Монтажёр" value={structuredDesc.staff.editor} studentId={structuredDesc.staff.editorId ?? null} onChange={(n, id) => patchStaff('editor', n, id)} warnings={warnings['staff.editor']} />
        <InlineField {...textFields[2]} />
        <InlineField {...textFields[3]} />
      </Stack>
      <Box style={{ flex: 1, minWidth: 0, paddingLeft: 'var(--mantine-spacing-md)', display: 'flex', flexDirection: 'column' }}>
        <Text fz="sm" fw={500} mb="sm">Предпросмотр описания</Text>
        <Paper radius="md" p="md" bg="var(--mantine-color-default)" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Text component="pre" c={generatedDescription ? 'var(--mantine-color-text)' : 'dimmed'} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', width: '100%', overflowY: 'auto', margin: 0, fontFamily: 'inherit' }}>
            {generatedDescription || '(Описание будет сгенерировано после заполнения полей)'}
          </Text>
        </Paper>
      </Box>
    </Group>
  );
}
